import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  OnInit,
  output,
  Output,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { HashMap, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Gateway } from '@wheere/api-site';
import { errorMessage } from '@wheere-front/shared-util';
import { MAPTILES_KEY } from '@wheere-front/shared-map-libre';
import { MessageService } from 'primeng/api';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { AlertDialogComponent, ButtonDirective, TooltipDirective, WhButtonComponent } from '@wheere-front/shared-ui';
import { SitePositionResult } from '../ui/site-position-dialog.component';
import { SiteRtkDialogComponent } from '../ui/site-rtk-dialog.component';
import { ConfigError, ConfigMessage, configMessage, renderConfigError, TranslateFn } from './config-message';
import { SITE_CONFIG_HOST, SiteConfigRtkPosition } from './site-config-host';
import { AntennaDraft, AntennaPosition, SiteConfigurationService } from './site-configuration.service';
import { DevicePickerDialogComponent } from './device-picker-dialog.component';
import { FilesSectionComponent, FilesState } from './files-section.component';
import { PositionDialogComponent, PositionDialogMode, PositionResult } from './position-dialog.component';
import { MapPreviewComponent } from './map-preview.component';
import { ReadinessBarComponent } from './readiness-bar.component';
import { GeneratedSignalsComponent } from './generated-signals.component';
import { SiteFrequencyStore } from './site-frequency.store';
import { antennaColour as frequencyAntennaColour } from './frequency-ui.components';

export type { AntennaPosition };

const UNKNOWN_ERROR = 'siteConfiguration.errors.unknown';

/** Ligne de CSV refusée : clé du message, la ligne fautive en paramètre. */
class CsvImportError extends Error {
  constructor(
    readonly key: string,
    readonly params: HashMap
  ) {
    super(key);
  }
}

/** Imports du gabarit, réutilisés tels quels par la sous-classe inside. */
export const SITE_CONFIGURATION_IMPORTS = [
  AlertDialogComponent,
  ButtonDirective,
  TooltipDirective,
  ReadinessBarComponent,
  MapPreviewComponent,
  DevicePickerDialogComponent,
  PositionDialogComponent,
  FilesSectionComponent,
  GeneratedSignalsComponent,
  SiteRtkDialogComponent,
  WhButtonComponent,
  FastSvgComponent,
  TranslocoModule,
];

export const SITE_CONFIGURATION_TEMPLATE = `
    <!-- Fixed-height shell (the parent content box does not scroll on this tab): banner,
         readiness bar and errors stay put; below lg the whole tab scrolls, from lg the left
         panel scrolls on its own and the right column (map + files) fits the viewport. -->
    <div *transloco="let t" class="flex h-full min-h-0 flex-col overflow-y-auto pt-6 lg:overflow-visible">
      <!-- Draft recovery banner -->
      @if (showDraftBanner() && !readonly()) {
        <div
          class="mb-2 flex items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/40 dark:bg-amber-500/10">
          <span class="text-xs text-amber-800 dark:text-amber-300">
            <i class="pi pi-history mr-1 text-xs"></i
            >{{ t('siteConfiguration.draft.unvalidated', { ago: renderMessage(draftTimeAgo(), t) }) }}
          </span>
          <div class="flex gap-1.5">
            <wh-button size="xs" userClass="!bg-amber-600 hover:!bg-amber-700" (click)="onRestoreDraft()"
              >{{ t('siteConfiguration.draft.restore') }}</wh-button
            >
            <wh-button
              variant="outline"
              size="xs"
              userClass="text-amber-700 hover:bg-amber-100 dark:bg-slate-700 dark:text-amber-300 dark:hover:bg-amber-500/20"
              (click)="onDiscardDraft()">
              {{ t('siteConfiguration.draft.discard') }}
            </wh-button>
          </div>
        </div>
      }

      <!-- Top bar: clear + readiness + validate (ADR-018: every site is auto-sync, no toggle). -->
      <lib-readiness-bar
        [readiness]="svc.readiness()"
        [loading]="saveLoading()"
        [dirty]="svc.isDirty()"
        [canValidate]="!readonly()"
        [canClear]="canClear()"
        (validate)="onValidate($event)"
        (clearSite)="onClearSite()">
      </lib-readiness-bar>

      <!-- Validation errors (inline, persistent) -->
      @if (validationErrors().length > 0) {
        <div
          class="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-500/40 dark:bg-red-500/10">
          @for (err of validationErrors(); track $index) {
            <div class="flex items-center gap-2 text-sm text-red-600 dark:text-red-300">
              <i class="pi pi-times-circle text-sm"></i>{{ renderError(err, t) }}
            </div>
          }
        </div>
      }

      <!-- ── Body — two columns on lg+ (7 / 5):
           • LEFT  — Gateways (one row per antenna).
           • RIGHT — Map preview on top, calibration files & size below.
         Under lg it stacks: gateways → map → files. -->
      <div class="mt-4 grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)]">
        <!-- LEFT column -->
        <div class="flex min-w-0 flex-col gap-4 lg:col-span-7 lg:min-h-0 lg:overflow-y-auto lg:pb-2 lg:pr-1">
          <!-- Gateways -->
          <section class="rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <header class="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-slate-700">
              <span class="text-warmYellow-500"><i class="pi pi-broadcast text-sm"></i></span>
              <h3 class="text-base font-semibold text-gray-800 dark:text-slate-100">
                {{ t('siteConfiguration.gateways.title') }}
                <span class="ml-1 text-sm font-normal text-gray-400 dark:text-slate-500"
                  >({{ svc.selectedGateways().length }})</span
                >
              </h3>
              <div class="flex-1"></div>
              <input
                #fileInput
                type="file"
                accept=".csv,.txt"
                style="display: none"
                (change)="onFileSelected($event)" />
              <button
                libBtn
                variant="ghost"
                size="xs"
                type="button"
                (click)="downloadCSVTemplate()"
                [libTooltip]="t('siteConfiguration.gateways.exportCsv')"
                [attr.aria-label]="t('siteConfiguration.gateways.exportCsv')"
                libTooltipPosition="bottom">
                <i class="pi pi-cloud-download text-sm"></i>
              </button>
              <button
                libBtn
                variant="ghost"
                size="xs"
                type="button"
                [disabled]="locked() || svc.selectedGateways().length === 0"
                (click)="fileInput.click()"
                [libTooltip]="t('siteConfiguration.gateways.importCsv')"
                [attr.aria-label]="t('siteConfiguration.gateways.importCsv')"
                libTooltipPosition="bottom">
                <i class="pi pi-cloud-upload text-sm"></i>
              </button>
              <span class="hidden text-[11px] text-gray-400 xl:inline dark:text-slate-500">{{
                t('siteConfiguration.frequency.antennaLimits')
              }}</span>
              <button
                libBtn
                variant="outline"
                size="xs"
                type="button"
                [disabled]="locked() || !svc.siteOrganizationId() || totalAntennas() >= 12"
                (click)="showGatewayPicker = true">
                <i class="pi pi-plus text-xs"></i>{{ t('siteConfiguration.gateways.add') }}
              </button>
            </header>

            <div class="flex flex-col gap-3 p-3">
              <!-- Site sans organisation : aucun device assignable (refus SITE_HAS_NO_ORGANIZATION). -->
              @if (!svc.siteOrganizationId()) {
                <p class="text-xs text-amber-700 dark:text-amber-300">
                  {{ 'sitePicker.siteHasNoOrganization' | transloco }}
                </p>
              }
              @if (svc.selectedGateways().length === 0) {
                <div
                  class="rounded-lg border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400 dark:border-slate-700 dark:text-slate-500">
                  {{ t('siteConfiguration.gateways.empty') }}
                </div>
              }

              @for (gw of svc.selectedGateways(); track gw.id; let i = $index) {
                <!-- One block per gateway: head row, optional gateway-position row, one row per antenna. -->
                <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
                  <div class="flex flex-wrap items-center gap-x-3 gap-y-2 bg-gray-50 px-3 py-2 dark:bg-slate-900/50">
                    <span
                      class="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 dark:bg-slate-800 dark:text-slate-400">
                      <fast-svg name="gateway" width="60%" height="60%" />
                    </span>
                    <div class="min-w-0 flex-1">
                      <span class="block truncate text-sm font-semibold text-gray-900 dark:text-slate-100">{{
                        gw.name || t('siteConfiguration.gateways.defaultName', { index: i })
                      }}</span>
                      <span class="block truncate text-xs tabular-nums text-gray-400 dark:text-slate-500">{{
                        gw.deviceId
                      }}</span>
                    </div>

                    <!-- Single-antenna gateway: the chassis position is the antenna's unless the
                       operator asks for a distinct one. Multi-antenna gateways always have one. -->
                    @if (antennasOf(gw).length === 1) {
                      <label class="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          class="text-primary-500 focus:ring-primary-500 size-3.5 rounded border-gray-300 dark:border-slate-600"
                          [checked]="svc.customGatewayPosition()[gw.id!] ?? false"
                          [disabled]="locked()"
                          (change)="svc.setCustomGatewayPosition(gw.id!, $any($event.target).checked)" />
                        {{ t('siteConfiguration.gateways.customPosition') }}
                      </label>
                    }

                    <div class="flex items-center gap-1.5 text-xs text-gray-600 dark:text-slate-300">
                      <span>{{
                        antennasOf(gw).length > 1
                          ? t('siteConfiguration.gateways.antennaCountMany', { count: antennasOf(gw).length })
                          : t('siteConfiguration.gateways.antennaCountOne', { count: antennasOf(gw).length })
                      }}</span>
                      <span class="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-slate-600">
                        <button
                          type="button"
                          class="inline-flex size-6 items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700"
                          [disabled]="locked() || antennasOf(gw).length <= 1"
                          (click)="svc.setAntennaCount(gw.id!, antennasOf(gw).length - 1)"
                          [attr.aria-label]="t('siteConfiguration.gateways.removeAntenna')">
                          −
                        </button>
                        <button
                          type="button"
                          class="inline-flex size-6 items-center justify-center border-l border-gray-300 text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                          [disabled]="locked() || antennasOf(gw).length >= 4 || totalAntennas() >= 12"
                          (click)="svc.setAntennaCount(gw.id!, antennasOf(gw).length + 1)"
                          [attr.aria-label]="t('siteConfiguration.gateways.addAntenna')">
                          +
                        </button>
                      </span>
                    </div>

                    <button
                      libBtn
                      variant="ghost"
                      size="xs"
                      type="button"
                      class="text-gray-400 hover:text-red-600 dark:text-slate-500 dark:hover:text-red-400"
                      [disabled]="locked()"
                      (click)="removeGateway(gw)"
                      [libTooltip]="t('siteConfiguration.gateways.remove')"
                      [attr.aria-label]="t('siteConfiguration.gateways.remove')"
                      libTooltipPosition="left">
                      <i class="pi pi-trash text-sm"></i>
                    </button>
                  </div>

                  <!-- Gateway (box) position — secondary information: the antennas are the reception
                       points, the box is only drawn for context. Small and muted, tucked under the head
                       row, so it never reads as one more antenna row. -->
                  @if (svc.isGatewayPositionCustom(gw.id) || antennasOf(gw).length > 1) {
                    <div
                      class="flex items-center gap-2 bg-gray-50 px-3 pb-1.5 pl-[3.25rem] text-[11px] text-gray-400 dark:bg-slate-900/50 dark:text-slate-500">
                      <i class="pi pi-box text-[10px]"></i>
                      <span>{{ t('siteConfiguration.gateways.boxPosition') }}</span>
                      @if (fmtPos(gw.latitude, gw.longitude, gw.altitude); as pos) {
                        <span class="truncate tabular-nums">{{ pos }}</span>
                      } @else {
                        <span class="text-amber-600 dark:text-amber-400">{{
                          t('siteConfiguration.gateways.boxPositionNotSet')
                        }}</span>
                      }
                      <button
                        type="button"
                        class="inline-flex size-5 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                        [disabled]="locked()"
                        (click)="openPositionDialog('gateway', gw, i)"
                        [libTooltip]="t('siteConfiguration.gateways.setBoxPosition')"
                        [attr.aria-label]="t('siteConfiguration.gateways.setBoxPosition')"
                        libTooltipPosition="left">
                        <i class="pi pi-map-marker text-[10px]"></i>
                      </button>
                      <button
                        type="button"
                        class="inline-flex size-5 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 disabled:opacity-30 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                        [disabled]="locked()"
                        (click)="openRTKDialog(gw, i)"
                        [libTooltip]="t('siteConfiguration.gateways.boxPositionRtk')"
                        [attr.aria-label]="t('siteConfiguration.gateways.boxPositionRtk')"
                        libTooltipPosition="left">
                        <i class="pi pi-globe text-[10px]"></i>
                      </button>
                    </div>
                  }

                  <!-- One row per antenna: colour swatch (same as the map), position, RF OUT pin, position button. -->
                  @for (a of antennasOf(gw); track a.orderGateway; let ai = $index) {
                    <div
                      class="grid grid-cols-[6.5rem_minmax(0,1fr)_auto] items-center gap-3 border-t border-gray-100 px-3 py-1.5 dark:border-slate-700">
                      <!-- Antenne Ai (ordre gateway puis antenne, ADR-039) · gateway · DAC (RF OUT, sinon rang). -->
                      <span
                        class="inline-flex items-center gap-1.5 text-xs font-bold text-gray-800 dark:text-slate-100"
                        [title]="t('siteConfiguration.frequency.antennaOrderHint')">
                        <span
                          class="inline-block size-2.5 shrink-0 rounded-[3px]"
                          [style.background]="antennaColour(i, ai)"></span>
                        <span class="flex min-w-0 flex-col leading-tight"
                          >A{{ globalAntennaIndex(i, ai) + 1 }}
                          <span
                            class="text-[10px] font-semibold"
                            [class]="dacConflict(i, ai) ? 'text-red-500' : 'text-gray-400 dark:text-slate-500'"
                            >GW{{ i }} · dac{{ a.rfOut ?? ai }}</span
                          ></span
                        >
                      </span>
                      @if (fmtPos(a.latitude, a.longitude, a.altitude); as pos) {
                        <span class="truncate text-sm tabular-nums text-gray-700 dark:text-slate-200">{{ pos }}</span>
                      } @else {
                        <span class="text-sm font-medium text-red-500 dark:text-red-400">{{
                          t('siteConfiguration.antennas.noPosition')
                        }}</span>
                      }
                      <span class="flex items-center gap-2">
                        <span
                          class="hidden text-[10px] font-semibold uppercase tracking-wider text-gray-400 sm:inline dark:text-slate-500"
                          >RF OUT</span
                        >
                        <!-- AUTO = positional default, or 0..3 explicit. Pins taken by sibling antennas grey out
                           (mirrors the backend (gateway_id, rf_out) partial-unique constraint). -->
                        <span
                          class="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-800"
                          role="radiogroup"
                          [attr.aria-label]="t('siteConfiguration.antennas.rfOutPin')">
                          <button
                            type="button"
                            role="radio"
                            [attr.aria-checked]="a.rfOut === null"
                            class="border-r border-gray-200 px-1.5 py-0.5 text-[11px] font-semibold transition-colors dark:border-slate-600"
                            [class]="
                              a.rfOut === null
                                ? 'bg-primary-500 text-white'
                                : 'text-gray-600 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-700'
                            "
                            [disabled]="locked()"
                            [title]="t('siteConfiguration.antennas.rfOutAuto', { position: ai })"
                            (click)="svc.setAntennaRfOut(gw.id!, ai, null)">
                            AUTO
                          </button>
                          @for (pin of [0, 1, 2, 3]; track pin) {
                            <button
                              type="button"
                              role="radio"
                              [attr.aria-checked]="a.rfOut === pin"
                              class="border-r border-gray-200 px-1.5 py-0.5 text-[11px] font-semibold transition-colors last:border-r-0 dark:border-slate-600"
                              [class]="
                                a.rfOut === pin
                                  ? 'bg-primary-500 text-white'
                                  : svc.isRfOutTaken(gw.id!, ai, pin)
                                    ? 'cursor-not-allowed bg-gray-50 text-gray-300 dark:bg-slate-900 dark:text-slate-600'
                                    : 'text-gray-600 hover:bg-gray-50 dark:text-slate-300 dark:hover:bg-slate-700'
                              "
                              [disabled]="locked() || (a.rfOut !== pin && svc.isRfOutTaken(gw.id!, ai, pin))"
                              [title]="
                                a.rfOut !== pin && svc.isRfOutTaken(gw.id!, ai, pin)
                                  ? t('siteConfiguration.antennas.rfOutTaken', { pin: pin })
                                  : 'RF OUT ' + pin
                              "
                              (click)="svc.setAntennaRfOut(gw.id!, ai, pin)">
                              {{ pin }}
                            </button>
                          }
                        </span>
                        <button
                          type="button"
                          class="inline-flex size-7 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                          [disabled]="locked()"
                          (click)="openAntennaPositionDialog(gw.id!, ai)"
                          [libTooltip]="t('siteConfiguration.antennas.setPosition')"
                          [attr.aria-label]="t('siteConfiguration.antennas.setPosition')"
                          libTooltipPosition="left">
                          <i class="pi pi-map-marker text-sm"></i>
                        </button>
                      </span>
                    </div>
                  }
                </div>
              }
            </div>
          </section>

          <!-- signal.json générés (ADR-039) : un par gateway, recalculés à chaque changement (inside). -->
          @if (!presetOnly()) {
            <lib-generated-signals />
          }
        </div>

        <!-- RIGHT column: map preview, then calibration files & size. From lg the column is
             locked to the viewport row: when the screen is too short for map (min 220px) +
             files card, it scrolls on its own instead of clipping the bottom of the files card. -->
        <div class="flex min-w-0 flex-col gap-4 lg:col-span-5 lg:min-h-0 lg:overflow-y-auto lg:pb-2 lg:pr-1">
          <!-- Map: 40vh when the tab stacks, otherwise whatever height the files card leaves. -->
          <div class="h-[40vh] shrink-0 lg:h-auto lg:min-h-[220px] lg:flex-1">
            <lib-map-preview
              class="block h-full"
              [gateways]="svc.selectedGateways()"
              [gatewayAntennas]="svc.gatewayAntennas()"
              [customGatewayIds]="customGatewayIds()"
              [antennaPositions]="svc.antennaPositions()"
              [draggable]="!readonly()"
              (gatewayDragged)="onGatewayDragged($event)">
            </lib-map-preview>
          </div>

          <section class="shrink-0 rounded-xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
            <header class="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 dark:border-slate-700">
              <span class="text-primary-500"><i class="pi pi-file text-sm"></i></span>
              <h3 class="text-base font-semibold text-gray-800 dark:text-slate-100">
                {{ t('siteConfiguration.files.title') }}
              </h3>
            </header>
            <div class="p-3">
              <lib-files-section
                #filesSection
                [siteId]="siteId()"
                [disabled]="locked()"
                [gatewayPositions]="allAntennaPositions()"
                [organizationId]="store.selected()?.organizationId ?? null"
                [activePresetId]="store.selected()?.activePresetId ?? null"
                [presetOnly]="presetOnly()"
                [calibrationLocked]="calibrationLocked()"
                [requestedPresetId]="requestedPresetId()"
                (filesChanged)="onFilesChanged($event)"
                (coverageChanged)="freqCoverage.set($event)">
              </lib-files-section>
            </div>
          </section>
        </div>
      </div>

      <!-- Dialogs -->
      <!-- RTK par défaut (integrator) : dialogue partagé. Inside garde le sien (requestRtkPosition). -->
      @if (rtkGatewayId()) {
        <lib-site-rtk-dialog
          [visible]="true"
          (visibleChange)="!$event && closeRtkDialog(null)"
          [gatewayId]="rtkGatewayId()"
          [gatewayName]="rtkGatewayName()"
          [maptilesKey]="maptilesKey"
          (positionPicked)="closeRtkDialog($event)" />
      }

      <lib-position-dialog [mode]="posDialogMode()" [(visible)]="showPosDialog" (positionSaved)="onPosSaved($event)">
      </lib-position-dialog>

      <lib-device-picker-dialog
        [header]="t('siteConfiguration.devicePicker.header')"
        deviceType="Gateways"
        [source]="svc.availableGateways()"
        [target]="svc.selectedGateways()"
        [(visible)]="showGatewayPicker"
        (selectionChanged)="onGwSelectionChanged($event)">
      </lib-device-picker-dialog>

      <!-- Confirmations use the shared alert dialog, like every other confirmation of the app. -->
      <lib-alert-dialog
        #validateDialog
        [title]="t('siteConfiguration.validateDialog.title')"
        [description]="t('siteConfiguration.validateDialog.description')"
        [confirmLabel]="t('siteConfiguration.validateDialog.confirm')"
        [cancelLabel]="t('siteConfiguration.cancel')"
        (confirm)="confirmValidate()" />
      <lib-alert-dialog
        #clearDialog
        [title]="t('siteConfiguration.clearDialog.title')"
        [description]="t('siteConfiguration.clearDialog.description')"
        [confirmLabel]="t('siteConfiguration.clearDialog.confirm')"
        [cancelLabel]="t('siteConfiguration.cancel')"
        variant="destructive"
        (confirm)="confirmClearSite()" />
    </div>
  `;

@Component({
  selector: 'lib-site-configuration',
  standalone: true,
  imports: SITE_CONFIGURATION_IMPORTS,
  // Fichier de fréquences du brouillon, partagé par la section Fichiers et les signal.json générés.
  providers: [SiteFrequencyStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: SITE_CONFIGURATION_TEMPLATE,
})
export class SiteConfigurationComponent implements OnInit {
  siteId = input<string | undefined>(undefined);
  /** Fichiers de calibration uniquement par preset (integrator) : ni « Custom upload », ni upload,
   * ni suppression ; le save n'envoie que `PATCH /sites/{id}/layout` avec `presetId`. */
  presetOnly = input<boolean>(false);
  /** Lecture seule (sans `site:configure-position`) : rien n'est modifiable ni validable. */
  readonly = input<boolean>(false);
  /** « Clear site » (`DELETE /sites/{id}/clean`, `site:write`). */
  canClear = input<boolean>(true);
  /** Site à fichiers personnalisés Wheere (sans preset) : partie fichiers verrouillée, le save les
   * conserve (pas de `presetId`). N'a de sens qu'avec {@link presetOnly}. */
  calibrationLocked = input<boolean>(false);
  /** Preset à appliquer (Site presets › « Appliquer à un site », inside) : mode preset, à valider. */
  requestedPresetId = input<string | null>(null);
  @Output() loadingChange = new EventEmitter<boolean>();
  /** Le site a changé côté serveur (validation réussie ou site vidé) : l'hôte peut recharger. */
  saved = output<void>();

  svc = inject(SiteConfigurationService);
  protected store = inject(SITE_CONFIG_HOST);
  protected cdr = inject(ChangeDetectorRef);
  private msg = inject(MessageService);
  /** Résolu à la demande (toasts seulement) : les specs d'inside montent ce composant sans transloco. */
  private readonly injector = inject(Injector);
  protected readonly maptilesKey = inject(MAPTILES_KEY, { optional: true }) ?? '';

  saveLoading = signal(false);
  /** Contrôles figés : sauvegarde en cours ou lecture seule. */
  protected readonly locked = computed(() => this.saveLoading() || this.readonly());
  /** Dialogue RTK partagé ouvert pour cette gateway (null = fermé). */
  protected readonly rtkGatewayId = signal<string | null>(null);
  protected readonly rtkGatewayName = signal('');
  #rtkResolve: ((p: SiteConfigRtkPosition | null) => void) | null = null;
  validationErrors = signal<ConfigError[]>([]);
  showGatewayPicker = false;
  showPosDialog = signal(false);
  posDialogMode = signal<PositionDialogMode>('gateway');
  showDraftBanner = signal(false);
  draftTimeAgo = signal<ConfigMessage>(configMessage('siteConfiguration.draft.ago.previousSession'));
  freqCoverage = signal<number | null>(null);

  /** Flat list of every antenna position across all gateways. The "Max GW dist" coverage
   *  metric must span the reception points (the antennas), NOT the gateway chassis — so this
   *  feeds both the files-section readout and {@link maxGwDistance}. */
  protected readonly allAntennaPositions = computed(() =>
    Object.values(this.svc.gatewayAntennas())
      .flat()
      .map(a => ({ latitude: a.latitude, longitude: a.longitude }))
  );

  maxGwDistance = computed(() => {
    const pts = this.allAntennaPositions().filter(p => p.latitude != null && p.longitude != null);
    if (pts.length < 2) return null;
    let max = 0;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const d = this.haversine(pts[i].latitude!, pts[i].longitude!, pts[j].latitude!, pts[j].longitude!);
        if (d > max) max = d;
      }
    }
    return Math.round(max * 10) / 10;
  });

  private curDeviceId: string | null = null;
  private curIdx: number | null = null;
  /** V8 — when editing an antenna position, also stash the owning gateway id so
   * onPosSaved can resolve which gateway's antenna we're updating. */
  private curGatewayId: string | null = null;

  /**
   * Template helper — returns the list of antenna drafts for one gateway.
   * Used by the per-gateway antenna section in the template (the +/− counter,
   * the antenna cards, and the RF OUT segmented control all iterate this).
   */
  antennasOf(g: Gateway): AntennaDraft[] {
    return g.id ? (this.svc.gatewayAntennas()[g.id] ?? []) : [];
  }

  /**
   * Flat antenna index across all gateways (gateway 0's antennas, then gateway 1's, …),
   * matching the order the map preview flattens them. Drives {@link antennaColour} so a
   * sidebar antenna badge and its map dot share the same palette colour.
   */
  protected globalAntennaIndex(gatewayIdx: number, antennaIdx: number): number {
    const gws = this.svc.selectedGateways();
    let acc = 0;
    for (let i = 0; i < gatewayIdx && i < gws.length; i++) {
      acc += this.antennasOf(gws[i]).length;
    }
    return acc + antennaIdx;
  }

  /** Palette accent for a sidebar antenna badge — same chartCustomColors cycle the map
   *  preview uses, keyed by the flat antenna index so badge ⇄ map dot colours match. */
  protected antennaColour(gatewayIdx: number, antennaIdx: number): string {
    return frequencyAntennaColour(this.globalAntennaIndex(gatewayIdx, antennaIdx));
  }

  /** Antennes du site (au plus 12, ADR-039). */
  protected readonly totalAntennas = computed(() =>
    Object.values(this.svc.gatewayAntennas()).reduce((n, list) => n + list.length, 0)
  );

  /** Deux antennes d'une même gateway sur le même DAC : la seconde est en conflit (refus serveur). */
  protected dacConflict(gatewayIdx: number, antennaIdx: number): boolean {
    const list = this.antennasOf(this.svc.selectedGateways()[gatewayIdx]);
    const dac = (k: number) => list[k]?.rfOut ?? k;
    for (let k = 0; k < antennaIdx; k++) if (dac(k) === dac(antennaIdx)) return true;
    return false;
  }

  /** Gateways that own a distinct, draggable chassis position (multi-antenna, or a
   *  single-antenna gateway whose custom-position selector is on). Passed to the map so
   *  only these render a gateway marker; implicit gateways track antenna 0. */
  /** "lat, lng · alt m" on one line, or null when the point has no coordinates yet. */
  protected fmtPos(lat?: number | null, lng?: number | null, alt?: number | null): string | null {
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return null;
    const a = alt != null && !isNaN(alt) ? ` · ${alt.toFixed(1)} m` : '';
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}${a}`;
  }

  /** Detach one gateway from the draft (same effect as unticking it in the picker). */
  protected removeGateway(gw: Gateway): void {
    const remaining = this.svc.selectedGateways().filter(g => g.id !== gw.id);
    this.onGwSelectionChanged({ source: [...this.svc.availableGateways(), gw], target: remaining });
  }

  protected readonly customGatewayIds = computed(() =>
    this.svc
      .selectedGateways()
      .filter(g => g.id && this.svc.isGatewayPositionCustom(g.id))
      .map(g => g.id!)
  );

  /** Persist a dragged gateway's new position onto the draft (keeps its existing
   *  altitude — the drag only moves it in the horizontal plane). */
  protected onGatewayDragged(evt: { gatewayId: string; lng: number; lat: number }): void {
    const gateways = this.svc.selectedGateways();
    const index = gateways.findIndex(g => g.id === evt.gatewayId);
    if (index < 0) return;
    this.svc.updateGatewayPosition(index, {
      latitude: evt.lat,
      longitude: evt.lng,
      altitude: gateways[index].altitude,
    });
  }

  /**
   * V8 — open the position dialog for a specific (gateway, antennaIndex) pair.
   * Replaces the old flat-index `openPositionDialog('antenna', null, i)` which
   * only worked in the LIGHT-only Antennas section.
   */
  openAntennaPositionDialog(gatewayId: string, antennaIndex: number): void {
    this.posDialogMode.set('antenna');
    this.curIdx = antennaIndex;
    this.curGatewayId = gatewayId;
    this.curDeviceId = null;
    this.showPosDialog.set(true);
    setTimeout(() => {
      if (!this.posDialog) return;
      const list = this.svc.gatewayAntennas()[gatewayId] ?? [];
      const a = list[antennaIndex];
      if (a) this.posDialog.patchValues(a.latitude, a.longitude, a.altitude);
      this.cdr.detectChanges();
    });
  }
  private files: FilesState = {
    frequencyFileName: '',
    frequencyRawContent: '',
    globalCalibFileName: '',
    globalCalibRawContent: '',
  };

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('filesSection') filesSection!: FilesSectionComponent;
  @ViewChild(PositionDialogComponent) posDialog!: PositionDialogComponent;

  ngOnInit(): void {
    const id = this.siteId();
    if (!id) return;

    this.svc.setSite(id);

    this.store.loadById(id).then(site => {
      const sizeOption = site?.size
        ? this.svc.SIZES.find(s => s.value === site.size) || this.svc.SIZES[2]
        : this.svc.SIZES[2];
      this.svc.selectedSize.set(sizeOption);
      this.filesSection?.setSizeFromStore(sizeOption);

      // Check for draft after store data is loaded
      if (this.svc.hasDraft()) {
        const ts = this.svc.getDraftTimestamp();
        this.draftTimeAgo.set(
          ts ? this.formatTimeAgo(ts) : configMessage('siteConfiguration.draft.ago.previousSession')
        );
        this.showDraftBanner.set(true);
      }

      // Capture initial state for dirty tracking
      setTimeout(() => this.svc.captureInitialState());
    });
  }

  isFormDirty(): boolean {
    return this.svc.isDirty();
  }

  onRestoreDraft(): void {
    this.svc.restoreDraft();
    this.showDraftBanner.set(false);
    this.cdr.detectChanges();
  }

  onDiscardDraft(): void {
    this.svc.clearDraft();
    this.showDraftBanner.set(false);
  }

  private formatTimeAgo(date: Date): ConfigMessage {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return configMessage('siteConfiguration.draft.ago.justNow');
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return configMessage('siteConfiguration.draft.ago.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return configMessage('siteConfiguration.draft.ago.hours', { count: hours });
    const days = Math.floor(hours / 24);
    return configMessage('siteConfiguration.draft.ago.days', { count: days });
  }

  /** Gabarit : texte d'une erreur (clé traduite, ou message brut du backend). */
  protected renderError(err: ConfigError, t: TranslateFn): string {
    return renderConfigError(err, t);
  }

  protected renderMessage(m: ConfigMessage, t: TranslateFn): string {
    return t(m.key, m.params);
  }

  /** Traduction impérative (toasts). */
  private translate(key: string, params?: HashMap): string {
    return this.injector.get(TranslocoService).translate(key, params);
  }

  private setLoading(v: boolean): void {
    this.saveLoading.set(v);
    this.loadingChange.emit(v);
  }

  onClearSite(): void {
    if (!this.canClear()) return;
    this.clearDialog()?.show();
  }

  protected async confirmClearSite(): Promise<void> {
    this.clearDialog()?.hide();
    const id = this.siteId();
    if (!id) return;
    // Échec : le store a affiché l'erreur, le site est intact côté serveur — on ne jette pas le
    // brouillon local en le réhydratant.
    if (!(await this.store.cleanSite(id))) return;
    this.saved.emit();
    // cleanSite only updates the backend — the local draft signals still hold the old
    // gateways/antennas. Reload the (now-empty) site and re-hydrate the draft
    // from it so the config UI clears immediately, then reset dirty tracking.
    const site = await this.store.loadById(id);
    this.svc.setSite(id);
    const sizeOption = site?.size
      ? this.svc.SIZES.find(s => s.value === site.size) || this.svc.SIZES[2]
      : this.svc.SIZES[2];
    this.svc.selectedSize.set(sizeOption);
    this.filesSection?.setSizeFromStore(sizeOption);
    this.svc.captureInitialState();
    this.cdr.detectChanges();
  }

  // --- Position ---
  openPositionDialog(mode: PositionDialogMode, device: Gateway | null, index: number): void {
    this.posDialogMode.set(mode);
    this.curIdx = index;
    this.curDeviceId = device && 'id' in device ? (device.id ?? null) : null;

    this.showPosDialog.set(true);

    // Patch after dialog renders
    setTimeout(() => {
      if (!this.posDialog) return;
      if (mode === 'gateway' && device) {
        const g = device as Gateway;
        this.posDialog.patchValues(g.latitude, g.longitude, g.altitude);
      } else if (mode === 'antenna') {
        const a = this.svc.antennaPositions()[index];
        if (a) this.posDialog.patchValues(a.latitude, a.longitude, a.altitude);
      }
      this.cdr.detectChanges();
    });
  }

  onPosSaved(r: PositionResult): void {
    const mode = this.posDialogMode();

    if (mode === 'gateway' && this.curDeviceId) {
      // Resolve index by device ID to handle reorder
      const idx = this.svc.selectedGateways().findIndex(g => g.id === this.curDeviceId);
      if (idx >= 0) {
        this.svc.updateGatewayPosition(idx, { latitude: r.lat, longitude: r.lng, altitude: r.altitude });
      }
    } else if (mode === 'antenna' && this.curIdx != null) {
      // V8 — antenna position now scoped to its owning gateway. curGatewayId is set
      // by openAntennaPositionDialog; the legacy openPositionDialog('antenna', null, i)
      // path (no gateway scope) is no longer reachable from the template.
      if (this.curGatewayId) {
        this.svc.updateAntennaPositionV8(this.curGatewayId, this.curIdx, {
          latitude: r.lat,
          longitude: r.lng,
          altitude: r.altitude,
        });
      } else {
        // Defensive fallback for any legacy caller — applies to the first gateway's
        // antennas (mirrors pre-V8 LIGHT behaviour).
        this.svc.updateAntennaPosition(this.curIdx, { latitude: r.lat, longitude: r.lng, altitude: r.altitude });
      }
    }

    this.curIdx = null;
    this.curDeviceId = null;
    this.curGatewayId = null;
    this.cdr.detectChanges();
  }

  /**
   * Relevé RTK de la position du boîtier. Par défaut le dialogue partagé `lib-site-rtk-dialog` ;
   * inside le remplace par le sien.
   */
  protected requestRtkPosition(device: Gateway): Promise<SiteConfigRtkPosition | null | undefined> {
    this.#rtkResolve?.(null);
    return new Promise(resolve => {
      this.#rtkResolve = resolve;
      this.rtkGatewayName.set(device.name ?? device.deviceId ?? '');
      this.rtkGatewayId.set(device.id ?? null);
    });
  }

  protected closeRtkDialog(r: SitePositionResult | null): void {
    const resolve = this.#rtkResolve;
    this.#rtkResolve = null;
    this.rtkGatewayId.set(null);
    resolve?.(r ? { latitude: r.lat, longitude: r.lng, altitude: r.altitude } : null);
  }

  async openRTKDialog(device: Gateway, index: number): Promise<void> {
    const deviceId = device.id!;
    try {
      const pos = await this.requestRtkPosition(device);
      if (!pos) return;

      // Resolve current index by ID after async wait
      const currentIdx = this.svc.selectedGateways().findIndex(g => g.id === deviceId);
      if (currentIdx >= 0) {
        this.svc.updateGatewayPosition(currentIdx, {
          latitude: pos.latitude,
          longitude: pos.longitude,
          altitude: pos.altitude,
        });
      }
      this.cdr.detectChanges();
    } catch (e) {
      // Ignoré.
    }
  }

  // --- Pickers ---
  onGwSelectionChanged(e: { source: any[]; target: any[] }): void {
    // updateSelectedGateways seeds the antennas of every newly-added gateway (ADR-019: one
    // topology, no per-mode hydration any more).
    this.svc.updateSelectedGateways([...e.target]);
    // Une gateway retirée du draft ne revient dans le vivier que si elle reste assignable
    // (organisation = celle du site) — sinon le PATCH layout la refuserait.
    this.svc.availableGateways.set(e.source.filter(g => this.svc.isAssignableGateway(g)));
    this.cdr.detectChanges();
  }

  onFilesChanged(state: FilesState): void {
    this.files = state;
  }

  // --- Validate ---
  onValidate(event: Event): void {
    if (this.readonly()) return;
    this.validationErrors.set([]);
    const errors = this.getErrors();
    if (errors.length > 0) {
      this.validationErrors.set(errors);
      return;
    }

    if (this.filesSection) {
      const fl = this.filesSection.validateFileLines();
      if (!fl.isValid) {
        this.validationErrors.set([fl.message ?? configMessage(UNKNOWN_ERROR)]);
        return;
      }
    }

    this.validateDialog()?.show();
  }

  private readonly validateDialog = viewChild<AlertDialogComponent>('validateDialog');
  private readonly clearDialog = viewChild<AlertDialogComponent>('clearDialog');

  protected confirmValidate(): void {
    this.validateDialog()?.hide();
    this.setLoading(true);
    const f = this.filesSection?.getFilesState() ?? this.files;
    this.doSave(f);
  }

  /**
   * Unified save — single path for every site, identical to the integrator (ADR-019).
   * Stores the frequency file / calib imported since the last save (ADR-039), then PATCHes the
   * unified layout (gateways + per-gateway antennas + RF OUT + size).
   */
  private doSave(f: FilesState): void {
    this.svc
      .saveViaLayout(
        this.svc.selectedSize() ?? undefined,
        {
          frequencyRawContent: f.frequencyRawContent,
          globalCalibRawContent: f.globalCalibRawContent,
          frequencyFileName: f.frequencyFileName,
          globalCalibFileName: f.globalCalibFileName,
        },
        // Persiste le preset choisi (mode "From preset") pour qu'il soit ré-affiché au reload.
        this.filesSection?.getActivePresetId() ?? null
      )
      .subscribe({
        next: r => {
          this.setLoading(false);
          if (r.success) {
            this.store.loadById(this.siteId()!);
            this.filesSection?.afterSave?.();
            this.svc.markClean();
            this.saved.emit();
            this.msg.add({ severity: 'success', summary: this.translate('siteConfiguration.toast.validated') });
          } else {
            this.validationErrors.set(
              r.errors?.length
                ? r.errors
                : [r.errorKey ? configMessage(r.errorKey) : r.error || configMessage(UNKNOWN_ERROR)]
            );
          }
        },
        error: e => {
          this.setLoading(false);
          // Message métier du backend s'il y en a un, sinon « erreur inconnue » traduite.
          this.validationErrors.set([errorMessage(e, '') || configMessage(UNKNOWN_ERROR)]);
        },
      });
  }

  /**
   * V8 validation — single rule set across all topologies. The legacy LIGHT/CLASSIC
   * branches have been collapsed to a single algorithm:
   *   1. At least one gateway selected.
   *   2. Every selected gateway has a position.
   *   3. Every gateway has at least one antenna, and every antenna has a position.
   *   4. Calibration files validation must pass.
   */
  private getErrors(): ConfigError[] {
    const errs: ConfigError[] = [];
    const gw = this.svc.selectedGateways();

    if (!gw.length) errs.push(configMessage('siteConfiguration.errors.selectGateway'));

    const gwMissingPos = gw.filter(g => g.latitude == null || g.longitude == null || g.altitude == null);
    if (gwMissingPos.length) {
      errs.push(configMessage('siteConfiguration.errors.gatewaysMissingPosition', { count: gwMissingPos.length }));
    }

    // Per-gateway antenna check. V8: every gateway has 1..4 antennas, each with its
    // own position. Missing positions block the validate.
    let antennasMissing = 0;
    for (const g of gw) {
      if (!g.id) continue;
      const ants = this.svc.gatewayAntennas()[g.id] ?? [];
      if (ants.length === 0) {
        const name = g.name || g.deviceId;
        errs.push(
          name
            ? configMessage('siteConfiguration.errors.gatewayHasNoAntenna', { name })
            : configMessage('siteConfiguration.errors.unnamedGatewayHasNoAntenna')
        );
        continue;
      }
      antennasMissing += ants.filter(a => a.latitude == null || a.longitude == null || a.altitude == null).length;
    }
    if (antennasMissing > 0) {
      errs.push(configMessage('siteConfiguration.errors.antennasMissingPosition', { count: antennasMissing }));
    }

    if (this.filesSection?.hasValidationError()) errs.push(configMessage('siteConfiguration.errors.frequency'));
    return errs;
  }

  // --- CSV ---
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      this.parseCSV(e.target?.result as string);
      input.value = '';
    };
    reader.readAsText(input.files[0]);
  }

  private parseCSV(content: string): void {
    try {
      const positions: { lat: number; lng: number; alt: number; antennaAlt?: number }[] = [];
      for (const line of content.trim().split('\n')) {
        const t = line.trim();
        if (!t || t.startsWith('#')) continue;
        const d = t.split('#')[0].trim();
        if (!d) continue;
        const p = d.split(',');
        if (p.length < 3 || p.length > 4) throw new CsvImportError('siteConfiguration.csv.badFormat', { line });
        const lat = parseFloat(p[0]),
          lng = parseFloat(p[1]),
          alt = parseFloat(p[2]);
        const aAlt = p.length === 4 ? parseFloat(p[3]) : undefined;
        if (isNaN(lat) || isNaN(lng) || isNaN(alt))
          throw new CsvImportError('siteConfiguration.csv.badNumbers', { line });
        if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          throw new CsvImportError('siteConfiguration.csv.outOfRange', { line });
        }
        positions.push({ lat, lng, alt, antennaAlt: aAlt });
      }

      // V8 — CSV rows map to the flat antenna list (each gateway then its
      // antennas, in order), same as the integrator's site config. Positions
      // live on the antennas for every site type; the gateway position mirrors
      // its antenna 0.
      const gateways = this.svc.selectedGateways();
      const antennaMap = this.svc.gatewayAntennas();
      const flatCount = gateways.reduce((n, g) => n + (antennaMap[g.id ?? '']?.length ?? 0), 0);
      if (positions.length < flatCount) {
        throw new CsvImportError('siteConfiguration.csv.notEnoughPositions', {
          positions: positions.length,
          antennas: flatCount,
        });
      }

      let idx = 0;
      gateways.forEach((g, gi) => {
        const list = antennaMap[g.id ?? ''] ?? [];
        list.forEach((_, ai) => {
          const p = positions[idx++];
          if (!p || !g.id) return;
          this.svc.updateAntennaPositionV8(g.id, ai, {
            latitude: p.lat,
            longitude: p.lng,
            altitude: p.alt,
          });
          if (ai === 0) {
            this.svc.updateGatewayPosition(gi, { latitude: p.lat, longitude: p.lng, altitude: p.alt });
          }
        });
      });

      this.msg.add({ severity: 'success', summary: this.translate('siteConfiguration.csv.imported') });
      this.cdr.detectChanges();
    } catch (e) {
      this.msg.add({
        severity: 'error',
        summary: this.translate('siteConfiguration.csv.parseError'),
        detail: e instanceof CsvImportError ? this.translate(e.key, e.params) : this.translate(UNKNOWN_ERROR),
      });
    }
  }

  downloadCSVTemplate(): void {
    // Mirror of parseCSV: one row per antenna (gateway by gateway, in order).
    let csv = '# lat,long,alt[,antennaAlt]\n';

    const antennaMap = this.svc.gatewayAntennas();
    for (const g of this.svc.selectedGateways()) {
      for (const ant of antennaMap[g.id ?? ''] ?? []) {
        csv += `${ant.latitude ?? 0},${ant.longitude ?? 0},${ant.altitude ?? 0}\n`;
      }
    }
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `site_${this.store.selected()?.name}_positions.csv`;
    a.click();
  }

  private haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}
